import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "@/redux/store";
import type {
  NotificationItem,
  NotificationReadSyncEvent,
  NotificationsListState,
  NotificationsState,
} from "../types/notification.types";

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  total: 0,
  hasMore: false,
  hasFetched: false,
  isSocketConnected: false,
};

function mergeNotification(items: NotificationItem[], nextItem: NotificationItem) {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);
  if (existingIndex === -1) {
    items.unshift(nextItem);
    return;
  }

  items.splice(existingIndex, 1, nextItem);
}

function appendUniqueNotifications(
  items: NotificationItem[],
  nextItems: NotificationItem[],
) {
  const existingIds = new Set(items.map((item) => item.id));

  for (const item of nextItems) {
    if (existingIds.has(item.id)) {
      continue;
    }

    items.push(item);
    existingIds.add(item.id);
  }
}

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    hydrateNotifications: (
      state,
      action: PayloadAction<NotificationsListState>,
    ) => {
      state.items = action.payload.items;
      state.total = action.payload.total;
      state.hasMore = action.payload.hasMore;
      state.hasFetched = true;
      state.unreadCount =
        typeof action.payload.unreadCount === "number"
          ? Math.max(0, action.payload.unreadCount)
          : action.payload.items.filter((item) => !item.is_read).length;
    },
    appendNotifications: (
      state,
      action: PayloadAction<NotificationsListState>,
    ) => {
      appendUniqueNotifications(state.items, action.payload.items);
      state.total = Math.max(action.payload.total, state.items.length);
      state.hasMore = action.payload.hasMore;
      state.hasFetched = true;

      if (typeof action.payload.unreadCount === "number") {
        state.unreadCount = Math.max(0, action.payload.unreadCount);
      }
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = Math.max(0, action.payload);
    },
    receiveNotification: (state, action: PayloadAction<NotificationItem>) => {
      const previous = state.items.find((item) => item.id === action.payload.id);
      mergeNotification(state.items, action.payload);
      state.total = Math.max(state.total, state.items.length);

      if (!previous && !action.payload.is_read) {
        state.unreadCount += 1;
        return;
      }

      if (previous && previous.is_read && !action.payload.is_read) {
        state.unreadCount += 1;
      }
    },
    markNotificationReadLocally: (state, action: PayloadAction<number>) => {
      const target = state.items.find((item) => item.id === action.payload);
      if (!target || target.is_read) {
        return;
      }

      target.is_read = true;
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    },
    markAllNotificationsReadLocally: (state) => {
      for (const item of state.items) {
        item.is_read = true;
      }

      state.unreadCount = 0;
    },
    applyReadSync: (
      state,
      action: PayloadAction<NotificationReadSyncEvent>,
    ) => {
      if ("read_all" in action.payload) {
        for (const item of state.items) {
          item.is_read = true;
        }

        state.unreadCount = 0;
        return;
      }

      const { id } = action.payload;
      const target = state.items.find((item) => item.id === id);
      if (target) {
        target.is_read = true;
      }

      state.unreadCount = state.items.filter((item) => !item.is_read).length;
    },
    setSocketConnected: (state, action: PayloadAction<boolean>) => {
      state.isSocketConnected = action.payload;
    },
    resetNotifications: () => initialState,
  },
});

export const {
  hydrateNotifications,
  appendNotifications,
  setUnreadCount,
  receiveNotification,
  markNotificationReadLocally,
  markAllNotificationsReadLocally,
  applyReadSync,
  setSocketConnected,
  resetNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;

export const selectNotificationsState = (state: RootState) => state.notifications;
export const selectNotifications = (state: RootState) => state.notifications.items;
export const selectUnreadNotificationCount = (state: RootState) =>
  state.notifications.unreadCount;
export const selectNotificationsFetched = (state: RootState) =>
  state.notifications.hasFetched;
export const selectNotificationsTotal = (state: RootState) => state.notifications.total;
export const selectNotificationsHasMore = (state: RootState) =>
  state.notifications.hasMore;
export const selectNotificationsSocketConnected = (state: RootState) =>
  state.notifications.isSocketConnected;
